export interface PatientAgeParts {
  years: number | string | null | undefined;
  months: number | string | null | undefined;
  days: number | string | null | undefined;
}

export interface NormalizedPatientAge {
  years: number;
  months: number;
  days: number;
}

export function toAgeNumber(value: unknown): number {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
}

export function normalizePatientAge(parts: PatientAgeParts): NormalizedPatientAge {
  let years = toAgeNumber(parts.years);
  let months = toAgeNumber(parts.months);
  let days = toAgeNumber(parts.days);

  if (days >= 30) {
    months += Math.floor(days / 30);
    days = days % 30;
  }

  if (months >= 12) {
    years += Math.floor(months / 12);
    months = months % 12;
  }

  return {
    years: Math.min(130, years),
    months: Math.min(11, months),
    days: Math.min(29, days),
  };
}

export function hasValidPatientAge(parts: PatientAgeParts, maxYears = 130): boolean {
  const normalized = normalizePatientAge(parts);
  const total = normalized.years + normalized.months + normalized.days;
  return total > 0 && normalized.years <= maxYears;
}

export function formatPatientAge(
  years?: number | string | null,
  months?: number | string | null,
  days?: number | string | null,
): string {
  const normalized = normalizePatientAge({ years, months, days });
  const { years: y, months: m, days: d } = normalized;

  if (y + m + d === 0) {
    return '—';
  }

  if (y >= 2) {
    return `${y} yrs`;
  }

  if (y === 1) {
    return m > 0 ? `1 yr ${m} mo` : '1 yr';
  }

  if (m > 0) {
    return d > 0 ? `${m} mo ${d} d` : `${m} mo`;
  }

  if (d > 0) {
    return d === 1 ? '1 day' : `${d} days`;
  }

  return `${y} yr`;
}

export function patientAgeToPayload(parts: PatientAgeParts): {
  age: number;
  age_months: number;
  age_days: number;
} {
  const normalized = normalizePatientAge(parts);
  return {
    age: normalized.years,
    age_months: normalized.months,
    age_days: normalized.days,
  };
}

export function readPatientAge(source: any): PatientAgeParts {
  return {
    years: source?.age ?? '',
    months: source?.age_months ?? '',
    days: source?.age_days ?? '',
  };
}
